import { Appointment } from '@chiroup/core/types/Appointment.type';
import { useInfiniteQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import locationService from '../services/location.service';

const PAGE_SIZE = 10;

type PastAppointmentsPage = {
  items: Appointment[];
  hasMore: boolean;
};

/**
 * Server-paginated history of past appointments. Each `fetchNextPage`
 * call hits `GET /appointment/{path}?past=true&offset=N&limit=10` so we
 * never pull more than one page from the DB at a time. The wrapper hook
 * is intentionally infinite-query-shaped — Appointments.tsx flattens
 * `data.pages` to render and uses `hasNextPage` / `fetchNextPage` to
 * drive the "Show more" button.
 *
 * `patientId` (optional) scopes the response to a single patient.
 * Family accounts share an email/phone, so without scoping the list
 * would mix every household member's history together.
 */
const usePastAppointments = (
  overridePath?: string | null,
  patientId?: string | null,
  clinicId?: number | null
) => {
  const { path: urlPath } = useParams();
  const path = overridePath || urlPath;

  return useInfiniteQuery<PastAppointmentsPage>(
    ['pastAppointments', path, clinicId || null, patientId || null],
    ({ pageParam = 0 }) => {
      // Path-based (legacy) first; authed portal falls back to clinicId
      // for clinics without a vanity URL.
      if (path) {
        return locationService.getPastAppointments(path, {
          offset: pageParam,
          limit: PAGE_SIZE,
          patientId,
        });
      }

      if (clinicId) {
        return locationService.getPastAppointmentsByClinicId(clinicId, {
          offset: pageParam,
          limit: PAGE_SIZE,
          patientId,
        });
      }

      return Promise.resolve({ items: [], hasMore: false });
    },
    {
      enabled: !!(path || clinicId),
      refetchOnWindowFocus: false,
      retry: false,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.hasMore ? allPages.length * PAGE_SIZE : undefined,
    }
  );
};

export default usePastAppointments;
export { PAGE_SIZE as PAST_PAGE_SIZE };
